import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  cartServiceHybrid as cartService,
  type CarritoData,
  type ProductoCarrito,
} from '../../clients/cart/services/cart-service-hybrid';
import carritoImg from '../../assets/logos/carrito-de-compras.png';

@customElement('carrito-cliente')
export class CarritoCliente extends LitElement {
  @property({ type: String }) url: string = '#/clients/cart';
  @state() private items: number = 0;

  private unsubscribe?: () => void;

  private onSessionReady = (_e: Event) => {
    // Refrescar el badge con lo último disponible — el hybrid maneja el sync
    this.items = this.contarLineas(cartService.getCarritoData());
  };
  private onAuthCleared = (_e: Event) => {
    // Si se limpió la sesión (logout, auth-failure), el contador va a cero
    this.items = 0;
  };
  private onAppLogout = (_e: Event) => {
    this.items = 0;
  };
  private onStorage = (e: StorageEvent) => {
    // Si otra pestaña modificó el carrito, reflejar el cambio en el badge
    if (e.key === 'carrito') {
      try {
        const data = e.newValue ? JSON.parse(e.newValue) : null;
        const productos = Array.isArray(data?.productos) ? data.productos : [];
        const snapshot: CarritoData = {
          productos,
          subtotal: 0, descuentos: 0, total: 0, cantidad_items: 0
        };
        this.items = this.contarLineas(snapshot);
      } catch {
        // si no se pudo parsear, no explotar
      }
    }
  };

  /* =================== ESTILOS CONSOLIDADOS =================== */
  /* Todos los estilos para carrito-cliente han sido consolidados en */
  /* global.css (Sección 22: ESTILOS CONSOLIDADOS PARA CARRITO-CLIENTE Y CREDITO-HEADER) */
  /* El componente utiliza Light DOM, por lo que los estilos globales */
  /* se aplican automáticamente sin necesidad de static styles. */
  static styles = css``;

  createRenderRoot() { return this; }

  /* ===== Helpers consistentes con cart-page ===== */

  private esCombo(p: Partial<ProductoCarrito> | any): boolean {
    if (p?.es_combo === true) return true;
    if (p?.combo_id && String(p.combo_id).trim() !== '' && String(p.combo_id) !== '0') return true;
    if (p?.cod_drog && String(p.cod_drog).toUpperCase().includes('COMBO')) return true;
    if (p?.combo) {
      const s = String(p.combo).toUpperCase().trim();
      if (['S', 'SI', 'YES', '1', 'TRUE', 'COMBO', 'COMBO_NORMAL', 'COMBO_OFERTA'].includes(s)) return true;
    }
    return false;
  }

  /** Clave canónica para fusionar líneas (igual criterio que en cart-page) */
  private buildLineKey(p: Partial<ProductoCarrito> | any): string {
    if (this.esCombo(p)) {
      const id = (p.combo_id ?? p.cod_drog ?? p.id ?? '').toString().trim().toUpperCase();
      return `C:${id}`;
    }
    const cod = (p.cod_drog ?? p.id ?? '').toString().trim().toUpperCase();
    const tipo = (p.tipo_solicitante ?? '').toString().trim().toUpperCase();
    return `P:${cod}:${tipo}`;
  }

  /** Cuenta renglones top-level únicos (ignora hijos de combo y dupes) */
  private contarLineas(data: CarritoData | undefined): number {
    if (!data || !Array.isArray(data.productos)) return 0;
    const keys = new Set<string>();
    for (const p of data.productos) {
      const esHijoDeCombo = p?.combo_id && !this.esCombo(p);
      if (esHijoDeCombo) continue;          // hijos no cuentan
      keys.add(this.buildLineKey(p));       // solo top-level
    }
    return keys.size;
  }

  connectedCallback() {
    super.connectedCallback();

    // 1) Estado inicial
    this.items = this.contarLineas(cartService.getCarritoData());

    // 2) Suscripción al store del carrito (reactivo)
    this.unsubscribe = cartService.subscribe((data: CarritoData) => {
      this.items = this.contarLineas(data);
    });

    // 3) Eventos de auth + sincronización cross-tab
    window.addEventListener('auth:session-ready', this.onSessionReady as any);
    window.addEventListener('auth:session-cleared', this.onAuthCleared as any);
    window.addEventListener('app:logout', this.onAppLogout as any);
    window.addEventListener('storage', this.onStorage as any);

    // El hybrid sincroniza al bootear — no hace falta sync extra aquí
  }

  disconnectedCallback() {
    // Desuscribir store
    if (this.unsubscribe) { this.unsubscribe(); this.unsubscribe = undefined; }

    // Quitar listeners globales
    window.removeEventListener('auth:session-ready', this.onSessionReady as any);
    window.removeEventListener('auth:session-cleared', this.onAuthCleared as any);
    window.removeEventListener('app:logout', this.onAppLogout as any);
    window.removeEventListener('storage', this.onStorage as any);

    super.disconnectedCallback();
  }

  render() {
    return html`
      <a data-link href=${this.url} class="btn btn-primary btn-sm position-relative p-2 my-auto cart-btn" title="Ver carrito">
        <img src="${carritoImg}" alt="Carrito" class="cart-icon" style="width: 24px; height: 24px; object-fit: contain;" />
        <span class="position-absolute badge rounded-pill bg-danger" style="top: -15px; right: -15px;">
          ${this.items}
          <span class="visually-hidden">items en carrito</span>
        </span>
      </a>
    `;
  }
}
